/**
 * Pure TEA update for outline rename (notes / database / chapters / scenes / acts).
 *
 * Shell: `useRenameEntityFlow.ts` (`useTea` + focus / rename Cmds).
 * Display name is captured at Start so scene rename (index-dependent labels)
 * matches chapter/note flows without special-casing.
 */
import { assertNever } from "../lib/assertNever";
import { isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type RenameEntityModel<T> = {
  readonly renaming: Maybe<T>;
  readonly draft: string;
  readonly originalDisplay: string;
};

export function initialRenameEntityModel<T>(): RenameEntityModel<T> {
  return {
    renaming: nothing,
    draft: "",
    originalDisplay: "",
  };
}

export type RenameEntityMsg<T> =
  | {
      readonly type: "Start";
      readonly id: T;
      readonly currentDisplay: string;
    }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "Commit" }
  | { readonly type: "Cancel" };

export type RenameEntityCmd<T> =
  | { readonly kind: "focusInput" }
  | { readonly kind: "rename"; readonly from: T; readonly to: string };

export function updateRenameEntity<T>(
  model: RenameEntityModel<T>,
  msg: RenameEntityMsg<T>,
  allowEmpty: boolean,
): readonly [RenameEntityModel<T>, readonly RenameEntityCmd<T>[]] {
  switch (msg.type) {
    case "Start":
      return [
        {
          renaming: just(msg.id),
          draft: msg.currentDisplay,
          originalDisplay: msg.currentDisplay,
        },
        [{ kind: "focusInput" }],
      ];
    case "DraftChanged":
      return [{ ...model, draft: msg.draft }, []];
    case "Cancel":
      return [initialRenameEntityModel(), []];
    case "Commit": {
      const fromM = model.renaming;
      const closed = initialRenameEntityModel<T>();
      if (isNothing(fromM)) {
        return [closed, []];
      }
      const to = model.draft.trim();
      // Unchanged (e.g. blur without editing) — renaming a still-default name
      // to its own displayed label would otherwise turn it into a custom name.
      if (
        (to.length === 0 && allowEmpty === false) ||
        to === model.originalDisplay
      ) {
        return [closed, []];
      }
      return [closed, [{ kind: "rename", from: fromM.value, to }]];
    }
    default:
      return assertNever(msg);
  }
}

export function makeRenameEntityUpdate<T>(
  allowEmpty: boolean,
): UpdateFn<RenameEntityModel<T>, RenameEntityMsg<T>, RenameEntityCmd<T>> {
  return (model, msg) => updateRenameEntity(model, msg, allowEmpty);
}
